import { UserRole } from '@/enums/user-role';
import { LoginRequest } from '@/models/login-request';
import { Person } from '@/models/person';
import { TokenResponse } from '@/models/token-response';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(private httpClient: HttpClient) {}

  login({ username, password }: LoginRequest) {
    return this.httpClient.post<TokenResponse>(`${environment.apiUrl}/api/v1/Authentication/login`, {
      username,
      password,
    });
  }

  refresh(token: string) {
    return this.httpClient.post<TokenResponse>(`${environment.apiUrl}/api/v1/Authentication/refresh`, token);
  }

  currentPerson() {
    return this.httpClient.get<Person>(`${environment.apiUrl}/api/v1/CurrentPerson`);
  }

  fetchRolesFromAccessToken(accessToken: string): UserRole[] | null {
    try {
      const decodedToken: Record<string, unknown> = jwtDecode(accessToken);
      const role = decodedToken['role'];

      if (typeof role === 'string') {
        return [role] as UserRole[];
      }

      return role as UserRole[];
    } catch (error) {
      console.warn('Error decoding access token:', error);
      return null;
    }
  }

  isTokenExpired(accessToken: string, leeway = 60): boolean {
    try {
      const decodedToken: Record<string, unknown> = jwtDecode(accessToken);
      const expirationTime = decodedToken['exp'] as number;
      const currentTime = Math.floor(Date.now() / 1000);

      return expirationTime - currentTime < leeway;
    } catch (error) {
      console.warn('Error decoding access token:', error);
      return true; // Assume expired if there's an error
    }
  }

  forgotPassword(username: string) {
    return this.httpClient.post<boolean>(`${environment.apiUrl}/api/v1/authentication/forgot-password`, { username });
  }

  resetPassword(email: string, resetToken: string, newPassword: string) {
    return this.httpClient.post<{ message: string }>(`${environment.apiUrl}/api/v1/authentication/reset-password`, {
      email,
      resetToken,
      newPassword,
    });
  }

  acceptInvite(token: string, password: string) {
    return this.httpClient.post<TokenResponse>(`${environment.apiUrl}/api/v1/authentication/accept-invite`, {
      token,
      password,
    });
  }
}
